import { formatDate } from 'utils';
import { SortOrder } from 'utils/helpers/sorting';

import { UsersFilterName } from 'services/specialist';

import {
  AssessmentCounter,
  ActivitiesCounter,
  TableColumn,
  TCDashboardTableActions,
  LinkSourceTooltip,
} from 'components';
import {
  PatientInfo,
  TableDataKeys,
} from 'pages/TherapyCompanionDashboard/types';

import { ConfigParams } from './types';

import * as S from './styles';

const tcDashboardTableConfig = ({
  intl,
  getRelativeTime,
  formatToDayAndMonth,
  showActivitiesByGeoCode,
}: ConfigParams) => {
  const handleRediretToEUPage = (cognitoId: string) => {
    window.open(`/new-provider/enduser/${cognitoId}`, '_blank');
  };

  const linkedFilterColumns = (
    defaultSortDataKey: TableDataKeys,
    defaultSort: SortOrder,
  ): TableColumn[] => {
    const checkDefaultSort = (dataKey: TableDataKeys) => {
      return defaultSortDataKey === dataKey ? defaultSort : undefined;
    };

    const getActivitiesColumns = (showActivities: boolean): TableColumn[] => {
      if (!showActivities) {
        return [];
      }

      return [
        {
          dataKey: 'lastActivity',
          key: 'enduser_last_activity',
          title: intl.formatMessage({
            id: 'DASHBOARD.TC.COLUMNTITLE.LASTACTIVITY',
          }),
          render: (lastActivity: string | undefined) => {
            if (lastActivity) {
              return getRelativeTime(lastActivity, true);
            }
            return '-';
          },
          sortable: true,
          defaultSort: checkDefaultSort('lastActivity'),
          sortTooltip: {
            enable: true,
            ascText: intl.formatMessage({
              id: 'DASHBOARD.TC.TOOLTIP.LMOSTTOLEASTRECENTACTIVITY',
            }),
            descText: intl.formatMessage({
              id: 'DASHBOARD.TC.TOOLTIP.LEASTTOMOSTRECENTACTIVITY',
            }),
          },
        },
        {
          dataKey: 'activities',
          key: 'enduser_activities',
          title: intl.formatMessage({
            id: 'DASHBOARD.TC.COLUMNTITLE.ACTIVITIES',
          }),
          render: (patient: PatientInfo) => {
            if (patient) {
              return (
                <ActivitiesCounter
                  cognitoId={patient.cognitoId}
                  doneActivities={patient.doneActivities}
                  totalActivities={patient.totalActivities}
                />
              );
            }

            return '-';
          },
          sortable: true,
          defaultSort: checkDefaultSort('doneActivities'),
          sortBy: 'doneActivitiesRatio',
          sortTooltip: {
            enable: true,
            ascText: intl.formatMessage({
              id: 'DASHBOARD.TC.TOOLTIP.MOSTTOLEASTCOMPLETEACTIVITY',
            }),
            descText: intl.formatMessage({
              id: 'DASHBOARD.TC.TOOLTIP.LEASTTOMOSTCOMPLETEACTIVITY',
            }),
          },
        },
      ];
    };

    return [
      {
        dataKey: 'fullName',
        key: 'enduser_fullname',
        title: intl.formatMessage({
          id: 'DASHBOARD.TC.COLUMNTITLE.NAME',
        }),
        render: (fullName: string | undefined, patient: PatientInfo) => {
          if (fullName && patient.cognitoId) {
            return (
              <S.FullnameText
                onClick={() =>
                  patient.cognitoId && handleRediretToEUPage(patient.cognitoId)
                }
                data-testid="patientFullName"
              >
                {fullName}
              </S.FullnameText>
            );
          }
          return '-';
        },
        width: '15%',
        sortable: true,
        defaultSort: checkDefaultSort('fullName'),
        sortTooltip: {
          enable: true,
          ascText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.ALPHABETICORDERNAME',
          }),
          descText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.REVERSEALPHABETICNAME',
          }),
        },
      },
      {
        dataKey: 'linkedAt',
        key: 'enduser_total_sessions',
        title: intl.formatMessage({
          id: 'DASHBOARD.TC.COLUMNTITLE.ARRIVAL',
        }),
        render: (linkedAt: string) => {
          return formatToDayAndMonth(linkedAt);
        },
        sortable: true,
        defaultSort: checkDefaultSort('linkedAt'),
        sortType: 'date',
        sortTooltip: {
          enable: true,
          ascText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.MOSTTOLEASTARRIVAL',
          }),
          descText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.LEASTTOMOSTARRIVAL',
          }),
        },
      },
      {
        dataKey: 'translatedReason',
        key: 'enduser_reason',
        title: intl.formatMessage({
          id: 'DASHBOARD.TC.COLUMNTITLE.SOURCE',
        }),
        render: (translatedReason: string, patientInfo: PatientInfo) => (
          <LinkSourceTooltip
            linkReason={patientInfo.reason}
            translatedReason={translatedReason}
          />
        ),
        sortable: true,
        defaultSort: checkDefaultSort('reason'),
        sortTooltip: {
          enable: true,
          ascText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.ALPHABETICORDERSOURCE',
          }),
          descText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.REVERSEALPHABETICSOURCE',
          }),
        },
      },
      {
        dataKey: 'phqValue',
        key: 'enduser_phq',
        title: intl.formatMessage({
          id: 'DASHBOARD.TC.COLUMNTITLE.PHQ9',
        }),
        render: (_, patientInfo: PatientInfo) => {
          return (
            <AssessmentCounter
              value={patientInfo.phqValue}
              result={patientInfo.phqResult}
            />
          );
        },
        sortable: true,
        defaultSort: checkDefaultSort('phqValue'),
        sortTooltip: {
          enable: true,
          ascText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.MOSTTOLEASTCRITICALPHQ9',
          }),
          descText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.LEASTTOMOSTCRITICALPHQ9',
          }),
        },
      },
      {
        dataKey: 'gadValue',
        key: 'enduser_gad',
        title: intl.formatMessage({
          id: 'DASHBOARD.TC.COLUMNTITLE.GAD7',
        }),
        render: (_, patientInfo: PatientInfo) => {
          return (
            <AssessmentCounter
              value={patientInfo.gadValue}
              result={patientInfo.gadResult}
            />
          );
        },
        sortable: true,
        defaultSort: checkDefaultSort('gadValue'),
        sortTooltip: {
          enable: true,
          ascText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.MOSTTOLEASTCRITICALGAD7',
          }),
          descText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.LEASTTOMOSTCRITICALGAD7',
          }),
        },
      },
      ...getActivitiesColumns(showActivitiesByGeoCode),
      {
        dataKey: 'lastMessage',
        key: 'enduser_last_message',
        title: intl.formatMessage({
          id: 'DASHBOARD.TC.COLUMNTITLE.MESSAGE',
        }),
        render: (lastActivity: string | undefined) => {
          if (lastActivity) {
            return getRelativeTime(lastActivity, true);
          }
          return '-';
        },
        sortable: true,
        defaultSort: checkDefaultSort('lastMessage'),
        sortTooltip: {
          enable: true,
          ascText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.MOSTTOLEASTRECENTMESSAGE',
          }),
          descText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.LEASTTOMOSTRECENTMESSAGE',
          }),
        },
      },
      {
        dataKey: 'actions',
        title: '',
        key: 'enduser_button_actions',
        render: (_, patient: PatientInfo) => {
          return <TCDashboardTableActions patient={patient} />;
        },
      },
    ];
  };

  const unlinkedFilterColumns = (
    defaultSortDataKey: TableDataKeys,
    defaultSort: SortOrder,
  ): TableColumn[] => {
    const checkDefaultSort = (dataKey: TableDataKeys) => {
      return defaultSortDataKey === dataKey ? defaultSort : undefined;
    };

    return [
      {
        dataKey: 'fullName',
        key: 'enduser_fullname',
        title: intl.formatMessage({
          id: 'DASHBOARD.TC.COLUMNTITLE.NAME',
        }),
        render: (fullName: string) => {
          return (
            <S.FullnameText data-testid="patientFullName-unlinked">
              {fullName}
            </S.FullnameText>
          );
        },
        width: '33%',
        sortable: true,
        defaultSort: checkDefaultSort('fullName'),
        sortTooltip: {
          enable: true,
          ascText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.ALPHABETICORDERNAME',
          }),
          descText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.REVERSEALPHABETICNAME',
          }),
        },
      },
      {
        dataKey: 'unlinkedAt',
        key: 'enduser_unlinkedAt',
        title: intl.formatMessage({
          id: 'DASHBOARD.TC.COLUMNTITLE.UNLINK',
        }),
        render: (unlinkedAt: string) => {
          return formatDate(unlinkedAt, { locale: intl.locale });
        },
        sortable: true,
        sortType: 'date',
        defaultSort: checkDefaultSort('unlinkedAt'),
        sortTooltip: {
          enable: true,
          ascText: intl.formatMessage({
            id: 'THERAPY_COMPANION.DASHBOARD.TABLE.UNLINKED_AT.SORT_TOOLTIP.ASC',
          }),
          descText: intl.formatMessage({
            id: 'THERAPY_COMPANION.DASHBOARD.TABLE.UNLINKED_AT.SORT_TOOLTIP.DESC',
          }),
        },
      },
      {
        dataKey: 'dischargeReason',
        key: 'enduser_dischargeReason',
        title: intl.formatMessage({
          id: 'DASHBOARD.TC.COLUMNTITLE.REASON',
        }),
        render: (dischargeReason: string) => dischargeReason,
        sortable: true,
        sortTooltip: {
          enable: true,
          ascText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.ALPHABETICORDERSOURCE',
          }),
          descText: intl.formatMessage({
            id: 'DASHBOARD.TC.TOOLTIP.REVERSEALPHABETICSOURCE',
          }),
        },
      },
      {
        dataKey: 'therapistName',
        key: 'enduser_therapistName',
        title: intl.formatMessage({
          id: 'DASHBOARD.TC.COLUMNTITLE.PROFESSIONAL',
        }),
        render: (therapistName: string) => {
          return therapistName;
        },
        sortable: true,
        defaultSort: checkDefaultSort('therapistName'),
        sortTooltip: {
          enable: true,
          ascText: intl.formatMessage({
            id: 'THERAPY_COMPANION.DASHBOARD.TABLE.THERAPIST.SORT_TOOLTIP.ASC',
          }),
          descText: intl.formatMessage({
            id: 'THERAPY_COMPANION.DASHBOARD.TABLE.THERAPIST.SORT_TOOLTIP.DESC',
          }),
        },
      },
    ];
  };

  const columnsFilterTypes: Partial<Record<UsersFilterName, TableColumn[]>> = {
    [UsersFilterName.NEW]: linkedFilterColumns('linkedAt', 'DESC'),
    [UsersFilterName.ENGAGED]: linkedFilterColumns('lastActivity', 'ASC'),
    [UsersFilterName.WARNING]: linkedFilterColumns('lastActivity', 'DESC'),
    [UsersFilterName.DISENGAGED]: linkedFilterColumns('lastActivity', 'DESC'),
    [UsersFilterName.NOT_INITIALIZED]: linkedFilterColumns(
      'lastMessage',
      'DESC',
    ),
    [UsersFilterName.FINISHING_PROGRAM]: linkedFilterColumns(
      'doneActivities',
      'DESC',
    ),
    [UsersFilterName.NOT_VINCULATED]: unlinkedFilterColumns(
      'unlinkedAt',
      'DESC',
    ),
  };

  return {
    columnsFilterTypes,
  };
};

export default tcDashboardTableConfig;
